import { RecoverOrderPaymentButton } from "./recover-order-payment-button";
import { shipmentReference } from "@/lib/shipment-reference";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, formatDistanceToNow } from "date-fns";
import {
  useShipmentById,
  useUpdateShipmentStatus,
  useShipmentLabels,
} from "@/api/shipments";
import { ShipmentStatus } from "@/api.types.v2";
import { ActivityTimeline } from "./activity-timeline";
import { useShipmentChangeRequests } from "@/api/shipment-change-requests";
import { ShipmentChangeRequestDialog } from "./shipment-change-request-dialog";
import { CancelShipmentDialog } from "./cancel-shipment-dialog";
import { useSession } from "@/services/auth0/session";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Package,
  MapPin,
  Truck,
  Copy,
  ExternalLink,
  Printer,
  Clock,
  DollarSign,
  User,
  Building,
  Phone,
  Mail,
  Scale,
  Ruler,
  FileText,
  RefreshCw,
  Ban,
  Tag,
  Edit,
} from "lucide-react";

interface ShipmentDetailsDialogProps {
  shipmentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusColors: Record<ShipmentStatus, string> = {
  [ShipmentStatus.PENDING_PAYMENT]: "bg-amber-100 text-amber-800",
  [ShipmentStatus.LABEL_READY]: "bg-blue-100 text-blue-800",
  [ShipmentStatus.PICKED_UP]: "bg-purple-100 text-purple-800",
  [ShipmentStatus.IN_TRANSIT]: "bg-indigo-100 text-indigo-800",
  [ShipmentStatus.AT_FACILITY]: "bg-cyan-100 text-cyan-800",
  [ShipmentStatus.OUT_FOR_DELIVERY]: "bg-yellow-100 text-yellow-800",
  [ShipmentStatus.DELIVERED]: "bg-green-100 text-green-800",
  [ShipmentStatus.AVAILABLE_FOR_PICKUP]: "bg-teal-100 text-teal-800",
  [ShipmentStatus.DELIVERY_ATTEMPTED]: "bg-orange-100 text-orange-800",
  [ShipmentStatus.EXCEPTION]: "bg-red-200 text-red-900",
  [ShipmentStatus.ON_HOLD]: "bg-amber-100 text-amber-800",
  [ShipmentStatus.RETURNED]: "bg-stone-100 text-stone-800",
  [ShipmentStatus.CANCELLED]: "bg-red-100 text-red-800",
};

const formatCurrency = (cents?: number) => {
  if (!cents) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
};

export function ShipmentDetailsDialog({
  shipmentId,
  open,
  onOpenChange,
}: ShipmentDetailsDialogProps) {
  const { token } = useSession();
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showChangeRequestDialog, setShowChangeRequestDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [newStatus, setNewStatus] = useState<ShipmentStatus | null>(null);

  const {
    data: shipment,
    isLoading,
    error,
  } = useShipmentById(shipmentId || "");
  const { data: labels } = useShipmentLabels(shipmentId || "");
  const { data: changeRequests } = useShipmentChangeRequests(
    open ? shipmentId : null
  );

  const updateStatusMutation = useUpdateShipmentStatus(shipmentId || "", {
    onSuccess: () => {
      toast.success("Status updated");
      setShowStatusDialog(false);
      setNewStatus(null);
    },
    onError: () => {
      toast.error("Failed to update status");
    },
  });

  const handleCopyTracking = (trackingNumber: string) => {
    void navigator.clipboard.writeText(trackingNumber);
    toast.success("Tracking number copied");
  };

  const handlePrintLabels = async (
    format: string,
    labelType: "carrier" | "internal"
  ) => {
    if (!shipmentId) return;
    try {
      const url = new URL(
        `${import.meta.env.VITE_API_URL}/api/v2/shipping/shipments/${shipmentId}/labels/download`
      );
      url.searchParams.set("format", format);
      url.searchParams.set("labelType", labelType);

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch labels");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch {
      toast.error("Failed to open labels");
    }
  };

  const handleStatusChange = (status: ShipmentStatus) => {
    setNewStatus(status);
    setShowStatusDialog(true);
  };

  const confirmStatusChange = () => {
    if (newStatus) {
      updateStatusMutation.mutate(newStatus);
    }
  };

  // Early return if dialog is closed or no shipmentId
  if (!open || !shipmentId) return null;

  // Pattern B: read role-tagged addresses[] junction (canonical source).
  const fromAddress = shipment?.addresses?.find((a) => a.type === "FROM");
  const toAddress = shipment?.addresses?.find((a) => a.type === "TO");

  // A label is INTERNAL when EZX printed it — which happens because the carrier
  // is self-rated and there is no provider API to buy one from. Otherwise EZX
  // BOUGHT it from an integration.
  const hasCarrierLabels = labels?.some((l) => !l.carrier?.selfRated) ?? false;
  const hasInternalLabels = labels?.some((l) => l.carrier?.selfRated) ?? false;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl max-h-[90dvh] overflow-y-auto bg-gray-50 p-0 gap-0 border-0">
          <DialogHeader className="bg-white p-4 pr-12 sm:p-6 sm:pr-12 rounded-t-lg border-b text-left">
            <DialogTitle className="text-xl leading-snug break-words">
              {isLoading ? (
                <Skeleton className="h-6 w-48 max-w-full" />
              ) : (
                shipmentReference(shipment?.shipmentNumber)
              )}
            </DialogTitle>
            {!isLoading && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {shipment?.order?.orderNumber && (
                  <span className="text-muted-foreground">
                    Order ORD-{shipment.order.orderNumber}
                  </span>
                )}
                {shipment?.status && (
                  <Badge
                    className={cn(statusColors[shipment.status])}
                    variant="secondary"
                  >
                    {shipment.status.replace(/_/g, " ")}
                  </Badge>
                )}
              </div>
            )}
            <DialogDescription asChild>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                {isLoading ? (
                  <Skeleton className="h-4 w-64 max-w-full" />
                ) : (
                  <>
                    {shipment?.trackingNumber && (
                      <div className="flex min-w-0 items-center gap-1">
                        <Tag className="h-3 w-3 shrink-0" />
                        <span className="break-all">
                          {shipment.trackingNumber}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 shrink-0"
                          aria-label="Copy tracking number"
                          onClick={() =>
                            handleCopyTracking(shipment.trackingNumber ?? "")
                          }
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    {shipment?.createdAt && (
                      <span className="text-muted-foreground">
                        Created{" "}
                        {formatDistanceToNow(new Date(shipment.createdAt), {
                          addSuffix: true,
                        })}
                      </span>
                    )}
                  </>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-[120px] w-full" />
                ))}
              </div>
              <Skeleton className="h-[300px] w-full" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-muted-foreground">
                Failed to load shipment details
              </p>
            </div>
          ) : (
            <div className="min-w-0 p-4 sm:p-6 space-y-4">
              {shipment?.deliveryNotes?.trim() && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Delivery instructions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm whitespace-pre-wrap break-words">
                    {shipment.deliveryNotes}
                  </CardContent>
                </Card>
              )}
              {/* Quick Actions Bar */}
              <div className="flex flex-wrap gap-2 bg-white p-4 rounded-lg border">
                {shipment &&
                  (shipment.status === ShipmentStatus.PENDING_PAYMENT ||
                    shipment.status === ShipmentStatus.EXCEPTION) && (
                    <RecoverOrderPaymentButton
                      orderId={shipment.orderId}
                      shipmentId={shipment.id}
                    />
                  )}
                <Select
                  value={shipment?.status}
                  onValueChange={(v) => handleStatusChange(v as ShipmentStatus)}
                >
                  <SelectTrigger
                    className="w-full sm:w-[220px]"
                    aria-label="Change shipment status"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Change Status">
                      {shipment?.status?.replace(/_/g, " ")}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(ShipmentStatus).map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasCarrierLabels && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrintLabels("PDF", "carrier")}
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    Carrier Label
                  </Button>
                )}
                {hasInternalLabels && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrintLabels("PDF", "internal")}
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    Internal Label
                  </Button>
                )}

                {shipment?.trackingUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(shipment.trackingUrl, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Track Package
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowChangeRequestDialog(true)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Request Changes
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <Ban className="h-4 w-4 mr-1" />
                  Cancel Shipment
                </Button>
              </div>

              {/* Info Cards Row */}
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,16rem),1fr))] gap-4">
                {/* Carrier & Service Info */}
                <Card className="min-w-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Truck className="h-4 w-4" />
                      Carrier & Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Carrier</p>
                      <p className="font-medium">
                        {shipment?.carrier?.name ||
                          shipment?.carrierCode ||
                          "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Service</p>
                      <p className="font-medium">
                        {shipment?.serviceCode || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pickup</p>
                      <p className="font-medium">
                        {shipment?.pickupBookingId ? "Attached" : "Drop-off"}
                      </p>
                    </div>
                    <div>
                      {/* Was "Access", rendering the carrier's `accessType`
                          string. That field conflated the CARRIER with the PIPE
                          used to reach it — ShipStation is not a carrier, it is
                          how EZX reaches UPS — and a carrier is reachable several
                          ways at once, so it was never one value. What this panel
                          actually wants to say is how EZX procures the label. */}
                      <p className="text-xs text-muted-foreground">
                        Procurement
                      </p>
                      <p className="font-medium">
                        {shipment?.carrier?.selfRated === undefined
                          ? "-"
                          : shipment.carrier.selfRated
                            ? "Self-rated (EZX prices it)"
                            : "Via integration (EZX buys the label)"}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Pricing Info */}
                <Card className="min-w-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Pricing
                    </CardTitle>
                  </CardHeader>
                  {/* "Buy" and "Margin" are GONE, and not because they were
                      hidden — because the columns behind them were deleted.

                      `Shipment.buyAmountCents` was a copy of `sellAmountCents`
                      until the ledger made it real, at which point it became a
                      live customer-facing margin leak in handlers that had never
                      changed. What a carrier is OWED is now a PAYABLE_ACCRUAL
                      FinancialEvent on the leg — reconcilable against money that
                      actually moved, which a mutable column never was.

                      Nothing is substituted. A zero here would read as "this
                      carrier earned nothing", which is its own lie. */}
                  <CardContent className="space-y-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Sell</p>
                      <p className="font-medium text-green-600">
                        {formatCurrency(shipment?.sellAmountCents)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Carrier payable
                      </p>
                      <p className="text-muted-foreground text-sm">
                        Settled from the ledger, not this row.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Timing Info */}
                <Card className="min-w-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Timing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="text-xs text-muted-foreground">Created</p>
                      <p className="font-medium whitespace-nowrap">
                        {shipment?.createdAt
                          ? format(new Date(shipment.createdAt), "MMM d, yyyy")
                          : "-"}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="text-xs text-muted-foreground">Ship Date</p>
                      <p className="font-medium whitespace-nowrap">
                        {shipment?.shipDate
                          ? format(new Date(shipment.shipDate), "MMM d, yyyy")
                          : "-"}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="text-xs text-muted-foreground">
                        Est. Delivery
                      </p>
                      <p className="font-medium whitespace-nowrap">
                        {shipment?.estimatedDelivery
                          ? format(
                              new Date(shipment.estimatedDelivery),
                              "MMM d, yyyy"
                            )
                          : "-"}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="text-xs text-muted-foreground">Delivered</p>
                      <p className="font-medium whitespace-nowrap">
                        {shipment?.actualDelivery
                          ? format(
                              new Date(shipment.actualDelivery),
                              "MMM d, yyyy"
                            )
                          : "-"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Addresses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* From Address */}
                <Card className="min-w-0">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-500" />
                      From (Sender)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm break-words">
                    {fromAddress ? (
                      <>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {String(fromAddress.contactName || "-")}
                          </span>
                        </div>
                        {fromAddress.contactCompany && (
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span>{String(fromAddress.contactCompany)}</span>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p>{fromAddress.address?.line1 ?? "-"}</p>
                            {(fromAddress.addressLine2 ??
                              fromAddress.address?.line2) && (
                              <p>
                                {fromAddress.addressLine2 ??
                                  fromAddress.address?.line2}
                              </p>
                            )}
                            <p>
                              {fromAddress.address?.city ?? ""},{" "}
                              {fromAddress.address?.province ?? ""}{" "}
                              {fromAddress.address?.postalCode ?? ""}
                            </p>
                            <p>{fromAddress.address?.country ?? ""}</p>
                          </div>
                        </div>
                        {fromAddress.contactPhone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span>{String(fromAddress.contactPhone)}</span>
                          </div>
                        )}
                        {fromAddress.contactEmail && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{String(fromAddress.contactEmail)}</span>
                          </div>
                        )}
                        {fromAddress.instructions && (
                          <div className="flex items-start gap-2 mt-2 p-2 bg-yellow-50 rounded">
                            <FileText className="h-4 w-4 text-yellow-600 mt-0.5" />
                            <span className="text-yellow-800 whitespace-pre-wrap">
                              {String(fromAddress.instructions)}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-muted-foreground">No sender address</p>
                    )}
                  </CardContent>
                </Card>

                {/* To Address */}
                <Card className="min-w-0">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-green-500" />
                      To (Recipient)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm break-words">
                    {toAddress ? (
                      <>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {String(toAddress.contactName || "-")}
                          </span>
                        </div>
                        {toAddress.contactCompany && (
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span>{String(toAddress.contactCompany)}</span>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p>{toAddress.address?.line1 ?? "-"}</p>
                            {(toAddress.addressLine2 ??
                              toAddress.address?.line2) && (
                              <p>
                                {toAddress.addressLine2 ??
                                  toAddress.address?.line2}
                              </p>
                            )}
                            <p>
                              {toAddress.address?.city ?? ""},{" "}
                              {toAddress.address?.province ?? ""}{" "}
                              {toAddress.address?.postalCode ?? ""}
                            </p>
                            <p>{toAddress.address?.country ?? ""}</p>
                          </div>
                        </div>
                        {toAddress.contactPhone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span>{String(toAddress.contactPhone)}</span>
                          </div>
                        )}
                        {toAddress.contactEmail && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{String(toAddress.contactEmail)}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-muted-foreground">
                        No recipient address
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Packages */}
              <Card className="min-w-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Packages ({shipment?.packages?.length ?? 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {shipment?.packages && shipment.packages.length > 0 ? (
                    <div className="space-y-3">
                      {shipment.packages.map((pkg, index) => (
                        <div
                          key={pkg.id}
                          className="flex items-center justify-between p-3 rounded-lg border bg-card"
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-muted text-sm font-medium">
                              {index + 1}
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-4 text-sm">
                                <span className="flex items-center gap-1">
                                  <Scale className="h-3 w-3 text-muted-foreground" />
                                  {pkg.weightGrams
                                    ? `${(pkg.weightGrams / 1000).toFixed(2)} kg`
                                    : "-"}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Ruler className="h-3 w-3 text-muted-foreground" />
                                  {pkg.lengthCm && pkg.widthCm && pkg.heightCm
                                    ? `${pkg.lengthCm} x ${pkg.widthCm} x ${pkg.heightCm} cm`
                                    : "-"}
                                </span>
                                {pkg.declaredValueCents && (
                                  <span className="flex items-center gap-1">
                                    <DollarSign className="h-3 w-3 text-muted-foreground" />
                                    {formatCurrency(pkg.declaredValueCents)}
                                  </span>
                                )}
                              </div>
                              {pkg.description && (
                                <p className="text-xs text-muted-foreground">
                                  {pkg.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {pkg.individualTrackingNumber && (
                              <Badge variant="outline" className="text-xs">
                                {pkg.individualTrackingNumber}
                              </Badge>
                            )}
                            {pkg.isFragile && (
                              <Badge variant="secondary" className="text-xs">
                                Fragile
                              </Badge>
                            )}
                            {pkg.requiresCooling && (
                              <Badge variant="secondary" className="text-xs">
                                Cooling
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No packages
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Labels */}
              {labels && labels.length > 0 && (
                <Card className="min-w-0">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Labels ({labels.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {labels.map((label) => (
                        <div
                          key={label.id}
                          className="flex items-center justify-between p-3 rounded-lg border bg-card"
                        >
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">
                              {label.carrier?.selfRated
                                ? "Internal"
                                : "Carrier"}
                            </Badge>
                            <div>
                              <p className="font-medium text-sm">
                                {label.trackingNumber}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {label.labelFormat} -{" "}
                                {format(
                                  new Date(label.purchasedAt),
                                  "MMM d, yyyy h:mm a"
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {label.voidedAt && (
                              <Badge variant="destructive" className="text-xs">
                                Voided
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                handleCopyTracking(label.trackingNumber)
                              }
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {changeRequests && changeRequests.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Change requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {changeRequests.map((request) => (
                        <li key={request.id} className="rounded-md border p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">{request.status}</Badge>
                            <span className="text-xs text-muted-foreground">
                              Submitted by{" "}
                              {request.submittedByActorType?.toLowerCase() ??
                                "customer"}
                            </span>
                            <span className="text-sm font-medium">
                              {request.requestTypes.join(", ")}
                            </span>
                          </div>
                          {request.deliveryInstructions != null && (
                            <p className="mt-2 whitespace-pre-wrap text-sm">
                              Requested instructions:{" "}
                              {request.deliveryInstructions ||
                                "Remove instructions"}
                            </p>
                          )}
                          {request.contactName != null && (
                            <p className="mt-2 text-sm">
                              Requested contact name:{" "}
                              {request.contactName || "Remove name"}
                            </p>
                          )}
                          {request.contactPhone != null && (
                            <p className="mt-2 text-sm">
                              Requested contact phone:{" "}
                              {request.contactPhone || "Remove phone"}
                            </p>
                          )}
                          {request.contactEmail != null && (
                            <p className="mt-2 text-sm">
                              Requested contact email:{" "}
                              {request.contactEmail || "Remove email"}
                            </p>
                          )}
                          {request.addressLine2Correction != null && (
                            <p className="mt-2 text-sm">
                              Requested unit:{" "}
                              {request.addressLine2Correction || "Remove unit"}
                            </p>
                          )}
                          {request.addressCorrection && (
                            <p className="mt-2 whitespace-pre-wrap text-sm">
                              Manual address request:{" "}
                              {request.addressCorrection}
                            </p>
                          )}
                          {request.appliedChanges.length > 0 && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              Applied: {request.appliedChanges.join(", ")}
                            </p>
                          )}
                          {request.status === "ACTIONED" &&
                            request.appliedChanges.length === 0 && (
                              <p className="mt-2 text-xs text-amber-800">
                                Historical request marked handled without a
                                recorded applied change. Verify the current
                                Shipment values.
                              </p>
                            )}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Activity Log */}
              <ActivityTimeline shipmentId={shipmentId} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Change Confirmation */}
      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the shipment status from{" "}
              <strong>{shipment?.status?.replace(/_/g, " ")}</strong> to{" "}
              <strong>{newStatus?.replace(/_/g, " ")}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmStatusChange}
              disabled={updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? "Updating..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Shipment Change Request Dialog */}
      {shipment && (
        <ShipmentChangeRequestDialog
          open={showChangeRequestDialog}
          onOpenChange={setShowChangeRequestDialog}
          shipment={shipment}
        />
      )}

      {/* Cancel Shipment Dialog */}
      {shipment && (
        <CancelShipmentDialog
          open={showCancelDialog}
          onOpenChange={setShowCancelDialog}
          shipment={shipment}
        />
      )}
    </>
  );
}
